import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import api from '../services/api';

const SystemMonitoring = () => {
  const [basicHealth, setBasicHealth] = useState({ status: 'Loading...' });
  const [detailedHealth, setDetailedHealth] = useState(null);
  const [deepHealth, setDeepHealth] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [apiErrors, setApiErrors] = useState({});
  const { user, authToken } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(t('common.accessDenied'));
      navigate('/dashboard');
    }
  }, [user, navigate, t]);

  // Function to get the authentication token
  const getAuthToken = () => {
    // First try to get token from context
    if (authToken) {
      return authToken;
    }
    
    // If not in context, try localStorage
    const localToken = localStorage.getItem('token');
    if (localToken) {
      return localToken;
    }
    
    return null;
  };

  // Function to format bytes to a human-readable format
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Function to format uptime to a human-readable format
  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  };

  // Fetch health data
  const fetchHealthData = async () => {
    try {
      // Reset errors
      setApiErrors({});
      
      // Fetch basic health status from the public endpoint that doesn't require auth
      const basicResponse = await api.get('/api/health/public');
      console.log('Basic health response:', basicResponse.data);
      setBasicHealth(basicResponse.data);

      // Get token
      const token = getAuthToken();
      
      if (!token) {
        console.error('No authentication token available');
        setApiErrors(prev => ({ 
          ...prev, 
          auth: 'No authentication token available. Please log in again.' 
        }));
        return;
      }

      // Ensure proper auth header format
      const headers = {
        Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
      };
      console.log('Using auth headers:', headers);

      try {
        // Fetch detailed health information
        const detailedResponse = await api.get('/api/health/details', { headers });
        console.log('Detailed health response:', detailedResponse.data);
        setDetailedHealth(detailedResponse.data);

        // Fetch deep health check
        const deepResponse = await api.get('/api/health/deep', { headers });
        console.log('Deep health response:', deepResponse.data);
        setDeepHealth(deepResponse.data);
      } catch (authError) {
        console.error('Authentication error:', authError);
        const status = authError.response?.status;
        const message = authError.response?.data?.error || authError.message;
        
        setApiErrors(prev => ({ 
          ...prev, 
          details: `Error fetching health details: ${status} - ${message}`,
          deep: `Error fetching deep health: ${status} - ${message}`
        }));

        if (status === 401) {
          toast.error('Authentication failed. Please log in again.');
        } else if (status === 403) {
          toast.error('You do not have permission to access health metrics.');
        }
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching health data:', error);
      setApiErrors(prev => ({ 
        ...prev, 
        general: `Failed to fetch health data: ${error.message}` 
      }));
      toast.error('Failed to fetch system health data');
    }
  };

  // Set up automatic refresh
  useEffect(() => {
    fetchHealthData();

    let interval;
    if (isRefreshing) {
      interval = setInterval(() => {
        fetchHealthData();
      }, refreshInterval * 1000);
    }

    return () => clearInterval(interval);
  }, [refreshInterval, isRefreshing]);

  // Handle refresh interval change
  const handleRefreshChange = (e) => {
    setRefreshInterval(Number(e.target.value));
  };

  // Toggle automatic refresh
  const toggleRefresh = () => {
    setIsRefreshing(!isRefreshing);
  };

  // Manually refresh data
  const handleManualRefresh = () => {
    fetchHealthData();
    toast.success('Health data refreshed');
  };

  // Status indicator component
  const StatusIndicator = ({ status }) => {
    const getColor = () => {
      switch (status?.toUpperCase()) {
        case 'UP':
          return 'bg-green-500';
        case 'DEGRADED':
          return 'bg-yellow-500';
        case 'DOWN':
          return 'bg-red-500';
        default:
          return 'bg-gray-500';
      }
    };

    return (
      <div className="flex items-center">
        <div className={`w-3 h-3 rounded-full mr-2 ${getColor()}`}></div>
        <span>{status}</span>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">{t('systemMonitoring.dashboard')}</h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2 flex items-center">
          <div className="flex items-center">
            <label htmlFor="refresh" className="mr-2 text-sm">{t('systemMonitoring.refreshEvery')}</label>
            <select 
              id="refresh" 
              value={refreshInterval} 
              onChange={handleRefreshChange}
              className="border rounded p-1 text-sm"
              disabled={!isRefreshing}
            >
              <option value="5">5s</option>
              <option value="15">15s</option>
              <option value="30">30s</option>
              <option value="60">1m</option>
              <option value="300">5m</option>
            </select>
          </div>
          
          <button 
            onClick={toggleRefresh}
            className={`px-3 py-1 rounded text-white text-sm ${isRefreshing ? 'bg-red-500' : 'bg-green-500'}`}
          >
            {isRefreshing ? t('systemMonitoring.stop') : t('systemMonitoring.start')}
          </button>
          
          <button 
            onClick={handleManualRefresh}
            className="px-3 py-1 bg-blue-500 rounded text-white text-sm"
          >
            {t('systemMonitoring.refreshNow')}
          </button>
        </div>
      </div>
      
      <div className="text-sm text-gray-500 mb-6">
        {t('systemMonitoring.lastRefreshed')} {lastRefresh.toLocaleTimeString()}
      </div>

      {apiErrors.auth && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 mb-6 rounded-md">
          <p><strong>{t('systemMonitoring.authError')}</strong> {apiErrors.auth}</p>
          <p className="text-sm mt-2">System metrics requiring authentication will not be available.</p>
        </div>
      )}
      
      {/* System Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">{t('systemMonitoring.systemStatus')}</h2>
          <StatusIndicator status={basicHealth?.status || 'UNKNOWN'} />
          <p className="mt-2 text-sm">{basicHealth?.message || t('systemMonitoring.noStatusMessage')}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">{t('systemMonitoring.database')}</h2>
          {detailedHealth?.components?.database ? (
            <>
              <StatusIndicator status={detailedHealth.components.database.status} />
              <p className="mt-2 text-sm">{detailedHealth.components.database.message}</p>
            </>
          ) : (
            <p className="text-sm italic text-gray-500">
              {apiErrors.details ? t('systemMonitoring.loadingStatus') : t('systemMonitoring.loadingStatus')}
            </p>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">{t('systemMonitoring.server')}</h2>
          {detailedHealth?.components?.server ? (
            <>
              <StatusIndicator status={detailedHealth.components.server.status} />
              <p className="mt-2 text-sm">Version: {detailedHealth.components.server.version}</p>
            </>
          ) : (
            <p className="text-sm italic text-gray-500">
              {apiErrors.details ? t('systemMonitoring.loadingStatus') : t('systemMonitoring.loadingStatus')}
            </p>
          )}
        </div>
      </div>
      
      {/* System Metrics */}
      {detailedHealth?.systemInfo && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">{t('systemMonitoring.systemMetrics')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.uptime')}</h3>
              <p className="text-lg font-semibold">{formatUptime(detailedHealth.systemInfo.uptime)}</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.memoryUsage')}</h3>
              <p className="text-lg font-semibold">
                {formatBytes(detailedHealth.systemInfo.memoryUsage.rss)} / {formatBytes(detailedHealth.systemInfo.totalMemory)}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${(detailedHealth.systemInfo.memoryUsage.rss / detailedHealth.systemInfo.totalMemory) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.freeMemory')}</h3>
              <p className="text-lg font-semibold">{formatBytes(detailedHealth.systemInfo.freeMemory)}</p>
              <div className="text-xs text-gray-500">
                {((detailedHealth.systemInfo.freeMemory / detailedHealth.systemInfo.totalMemory) * 100).toFixed(1)}% {t('systemMonitoring.free')}
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.cpuCores')}</h3>
              <p className="text-lg font-semibold">{detailedHealth.systemInfo.cpus}</p>
              <p className="text-xs text-gray-500">
                {t('systemMonitoring.loadAverage')}: {detailedHealth.systemInfo.loadAverage.map(load => load.toFixed(2)).join(' | ')}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Database Performance */}
      {deepHealth?.components?.database && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">{t('systemMonitoring.databasePerformance')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.status')}</h3>
              <StatusIndicator status={deepHealth.components.database.status} />
            </div>
            
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.responseTime')}</h3>
              <p className="text-lg font-semibold">
                {deepHealth.components.database.responseTime}ms
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="text-sm font-medium text-gray-500">{t('systemMonitoring.queryTest')}</h3>
              <p className="text-lg font-semibold">
                {deepHealth.components.database.result || 'N/A'}
              </p>
            </div>
          </div>
          
          {deepHealth.components.database.error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-600">
              <h3 className="font-medium mb-1">{t('systemMonitoring.databaseError')}</h3>
              <p className="text-sm">{deepHealth.components.database.error}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Raw Data Section (Collapsible) */}
      <div className="bg-white rounded-lg shadow mb-8">
        <button 
          className="w-full text-left p-4 font-medium focus:outline-none flex justify-between items-center"
          onClick={() => document.getElementById('rawData').classList.toggle('hidden')}
        >
          <span>{t('systemMonitoring.rawData')}</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        <div id="rawData" className="hidden p-4 border-t">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="font-medium mb-2">{t('systemMonitoring.basicHealth')}</h3>
              <pre className="bg-gray-100 p-2 rounded overflow-auto text-xs">{JSON.stringify(basicHealth, null, 2)}</pre>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">{t('systemMonitoring.detailedHealth')}</h3>
              <pre className="bg-gray-100 p-2 rounded overflow-auto text-xs h-64">
                {detailedHealth ? JSON.stringify(detailedHealth, null, 2) : apiErrors.details || t('common.loading')}
              </pre>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">{t('systemMonitoring.deepHealth')}</h3>
              <pre className="bg-gray-100 p-2 rounded overflow-auto text-xs h-64">
                {deepHealth ? JSON.stringify(deepHealth, null, 2) : apiErrors.deep || t('common.loading')}
              </pre>
            </div>
          </div>
          
          {Object.keys(apiErrors).length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-600">
              <h3 className="font-medium mb-1">{t('systemMonitoring.apiErrors')}</h3>
              <pre className="bg-gray-100 p-2 rounded overflow-auto text-xs">{JSON.stringify(apiErrors, null, 2)}</pre>
            </div>
          )}
          
          <div className="mt-4">
            <h3 className="font-medium mb-2">{t('systemMonitoring.authTokenInfo')}</h3>
            <pre className="bg-gray-100 p-2 rounded overflow-auto text-xs">
              {getAuthToken() ? 
                `${t('systemMonitoring.tokenAvailable')} ${getAuthToken().substring(0, 15)}...${getAuthToken().substring(getAuthToken().length - 10)}` : 
                t('systemMonitoring.noAuthToken')}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitoring; 